import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { matchesSkillQuery } from '../utils/skillSearch';
import SkillFormModal from '../components/SkillFormModal';
import './booking/booking-shared.css';
import './Skills.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Click-to-edit cell. Renders display value until clicked; then becomes an input.
// Enter or blur saves; Escape cancels. type='number' for numeric fields.
function EditableCell({ value, type = 'text', step, onSave, disabled, monospace, formatDisplay }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [errored, setErrored] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);

  const commit = async () => {
    if (saving) return;
    const normalized = type === 'number'
      ? (draft === '' || draft == null ? null : Number(draft))
      : (draft === '' ? null : String(draft));
    const current = value ?? null;
    if (normalized === current) { setEditing(false); return; }
    setSaving(true);
    setErrored(false);
    try {
      await onSave(normalized);
      setEditing(false);
    } catch (e) {
      setErrored(true);
    } finally {
      setSaving(false);
    }
  };

  if (editing && !disabled) {
    return (
      <input
        ref={inputRef}
        type={type}
        step={step}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
        }}
        disabled={saving}
        style={{
          width: '100%',
          padding: '0.25rem 0.4rem',
          border: `1px solid ${errored ? '#e74c3c' : 'var(--booking-accent)'}`,
          borderRadius: 3,
          fontSize: '0.9rem',
          fontFamily: monospace ? 'monospace' : 'inherit',
        }}
      />
    );
  }

  const display = formatDisplay ? formatDisplay(value) : (value == null || value === '' ? '—' : String(value));
  return (
    <span
      className={disabled ? undefined : 'sk-editable'}
      onClick={() => !disabled && setEditing(true)}
      style={{
        fontFamily: monospace ? 'monospace' : 'inherit',
        opacity: saving ? 0.5 : 1,
        color: errored ? '#e74c3c' : 'inherit',
      }}
      title={disabled ? '' : 'Click to edit'}
    >
      {display}
    </span>
  );
}

export default function Skills() {
  // Everyone with skill tracking reads the library; changing it needs skill setup
  const { can } = useAuth();
  const canEditSkills = can('skillSetup', 'MANAGE');
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [editing, setEditing] = useState(null); // skill object or null
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState(null); // skill object pending confirm, or null
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/skills`, {
        params: showArchived ? { includeArchived: true } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setSkills(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const patchSkill = useCallback(async (skillId, patch) => {
    const token = localStorage.getItem('token');
    const res = await axios.put(`${API_BASE}/api/skills/${skillId}`, patch, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setSkills(prev => prev.map(s => s.id === skillId ? { ...s, ...res.data } : s));
  }, []);

  const handleSaveEdit = async (data) => {
    if (!editing) return;
    try {
      await patchSkill(editing.id, data);
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save skill');
    }
  };

  const handleArchive = async (skill) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/skills/${skill.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // If we're not showing archived, drop from view; otherwise update in place.
      setSkills(prev => showArchived
        ? prev.map(s => s.id === skill.id ? { ...s, archivedAt: res.data.archivedAt } : s)
        : prev.filter(s => s.id !== skill.id));
      setArchiving(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to archive skill');
      setArchiving(null);
    }
  };

  const handleRestore = async (skill) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/skills/${skill.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSkills(prev => prev.map(s => s.id === skill.id ? { ...s, archivedAt: res.data.archivedAt } : s));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore skill');
    }
  };

  const handleCreate = async (data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/skills`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Reload to get the full shape with levels[]/_count etc.
      await load();
      setCreating(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create skill');
    }
  };

  const levelOptions = useMemo(() => {
    const seen = new Map();
    for (const s of skills) {
      for (const l of (s.levels || [])) {
        if (l.id && !seen.has(l.id)) seen.set(l.id, l);
      }
    }
    return Array.from(seen.values()).sort((a, b) => (a.number || 0) - (b.number || 0));
  }, [skills]);

  const filtered = useMemo(() => {
    return skills.filter(s => {
      if (levelFilter === '__library__') {
        if ((s.levels || []).length > 0) return false;
      } else if (levelFilter) {
        if (!(s.levels || []).some(l => l.id === levelFilter)) return false;
      }
      return matchesSkillQuery(search, s.name, s.figNotation);
    });
  }, [skills, search, levelFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      let va, vb;
      switch (key) {
        case 'name':       va = a.name || ''; vb = b.name || ''; break;
        case 'fig':        va = a.figNotation || ''; vb = b.figNotation || ''; break;
        case 'difficulty': va = a.difficulty != null ? Number(a.difficulty) : -1;
                           vb = b.difficulty != null ? Number(b.difficulty) : -1; break;
        case 'dmtDifficulty': va = a.dmtDifficulty != null ? Number(a.dmtDifficulty) : -1;
                              vb = b.dmtDifficulty != null ? Number(b.dmtDifficulty) : -1; break;
        default:           va = 0; vb = 0;
      }
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const headerCell = (key, label) => (
    <th onClick={() => setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
      aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      {label}{sort.key === key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
    </th>
  );

  const dd = (v) => (v == null ? '—' : Number(v).toFixed(1));

  const rowActions = (s) => (
    <div className="sk-actions">
      <button type="button" onClick={() => setEditing(s)} className="bk-btn bk-btn--sm bk-btn--outline"
        aria-label={`Edit ${s.name}`}>Edit</button>
      {s.archivedAt ? (
        <button type="button" onClick={() => handleRestore(s)} className="bk-btn bk-btn--sm bk-btn--outline"
          title="Undo the archive" aria-label={`Restore ${s.name}`}>Restore</button>
      ) : (
        <button type="button" onClick={() => setArchiving(s)} className="bk-btn bk-btn--sm bk-btn--outline"
          title="Hides the skill from lookups; routines and progress records are kept" aria-label={`Archive ${s.name}`}>Archive</button>
      )}
    </div>
  );

  return (
    <div className="bk-page sk-page">
      <div className="sk-header">
        <h2 style={{ margin: 0 }}>All skills</h2>
        {canEditSkills && (
          <div className="sk-header__actions">
            <button type="button" onClick={() => setCreating(true)} className="bk-btn bk-btn--primary bk-btn--sm">
              + New skill
            </button>
          </div>
        )}
      </div>

      {error && <p className="bk-error">{error}</p>}

      <div className="sk-filters">
        <input
          type="search"
          className="bk-input sk-search"
          placeholder="Search by name or FIG notation"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search skills"
        />
        <select className="bk-input sk-level" value={levelFilter} onChange={e => setLevelFilter(e.target.value)} aria-label="Level">
          <option value="">All levels</option>
          <option value="__library__">Library only (not in a level)</option>
          {levelOptions.map(l => (
            <option key={l.id} value={l.id}>L{l.identifier}: {l.name}</option>
          ))}
        </select>
        {canEditSkills && (
          <label className="sk-archived">
            <input type="checkbox" className="bk-checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        )}
      </div>

      {loading && <p>Loading…</p>}

      {!loading && (
        <>
          <p className="sk-count">{sorted.length} skill{sorted.length === 1 ? '' : 's'}</p>

          {/* Desktop: sortable table with click-to-edit cells */}
          <table className="sk-table">
            <thead>
              <tr>
                {headerCell('name', 'Name')}
                {headerCell('fig', 'FIG')}
                {headerCell('difficulty', 'Trampoline')}
                {headerCell('dmtDifficulty', 'Double mini')}
                {canEditSkills && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => (
                <tr key={s.id} style={{ opacity: s.archivedAt ? 0.55 : 1 }}>
                  <td>
                    <EditableCell value={s.name} disabled={!canEditSkills} onSave={(v) => patchSkill(s.id, { name: v })} />
                    {s.prepSkill && <span className="sk-prep">Prep skill</span>}
                  </td>
                  <td>
                    <EditableCell value={s.figNotation} disabled={!canEditSkills} monospace
                      onSave={(v) => patchSkill(s.id, { figNotation: v })} />
                  </td>
                  <td>
                    <EditableCell
                      value={s.difficulty != null ? Number(s.difficulty) : null}
                      type="number"
                      step="0.1"
                      disabled={!canEditSkills}
                      formatDisplay={dd}
                      onSave={(v) => patchSkill(s.id, { difficulty: v })}
                    />
                  </td>
                  <td>
                    <EditableCell
                      value={s.dmtDifficulty != null ? Number(s.dmtDifficulty) : null}
                      type="number"
                      step="0.1"
                      disabled={!canEditSkills}
                      formatDisplay={dd}
                      onSave={(v) => patchSkill(s.id, { dmtDifficulty: v })}
                    />
                  </td>
                  {canEditSkills && <td>{rowActions(s)}</td>}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={canEditSkills ? 5 : 4} className="sk-muted" style={{ textAlign: 'center' }}>No skills match.</td></tr>
              )}
            </tbody>
          </table>

          {/* Phones: one compact row per skill; tap it to edit */}
          <ul className="sk-list bk-card">
            {sorted.map(s => {
              const body = (
                <>
                  <span className="sk-name">{s.name}</span>
                  <span className="sk-meta">
                    {s.figNotation && <span style={{ fontFamily: 'monospace' }}>{s.figNotation}</span>}
                    <span>TRA {dd(s.difficulty)}</span>
                    <span>DMT {dd(s.dmtDifficulty)}</span>
                    {s.prepSkill && <span>Prep skill</span>}
                    {s.archivedAt && <span>Archived</span>}
                  </span>
                </>
              );
              return (
                <li key={s.id} style={{ opacity: s.archivedAt ? 0.55 : 1 }}>
                  {canEditSkills
                    ? <button type="button" className="sk-row" onClick={() => setEditing(s)}>{body}</button>
                    : <div className="sk-row">{body}</div>}
                </li>
              );
            })}
            {sorted.length === 0 && <li className="sk-muted" style={{ padding: '0.75rem 0' }}>No skills match.</li>}
          </ul>
        </>
      )}

      {creating && (
        <SkillFormModal
          mode="add"
          showOrder={false}
          onSave={handleCreate}
          onCancel={() => setCreating(false)}
        />
      )}
      {editing && (
        <SkillFormModal
          mode="edit"
          skill={editing}
          showOrder={false}
          onSave={handleSaveEdit}
          onCancel={() => setEditing(null)}
          extraActions={editing.archivedAt
            ? <button type="button" className="bk-btn bk-btn--outline" onClick={() => { handleRestore(editing); setEditing(null); }}>Restore</button>
            : <button type="button" className="bk-btn bk-btn--outline-danger" onClick={() => { setArchiving(editing); setEditing(null); }}>Archive</button>}
        />
      )}
      {archiving && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3>Archive skill</h3>
              <button onClick={() => setArchiving(null)} className="close-button">×</button>
            </div>
            <div style={{ padding: '0.75rem 0' }}>
              <p style={{ margin: 0 }}>Archive <strong>{archiving.name}</strong>?</p>
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                The skill disappears from the routine search and library lookup. Existing routine references and gymnast progress records are preserved. You can restore it any time via the "Show archived" filter.
              </p>
              {archiving.routineCount > 0 && (
                <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  Currently used in {archiving.routineCount} routine{archiving.routineCount === 1 ? '' : 's'}, which keep working.
                </p>
              )}
              {(archiving.levels || []).length > 0 && (
                <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  Attached to: {archiving.levels.map(l => `L${l.identifier}`).join(', ')}.
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setArchiving(null)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => handleArchive(archiving)}>Archive</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
